/**
 * Pending File Store + data-URL helpers.
 *
 * All new uploads (images AND videos) are kept as blob URLs while the user
 * edits. This in-memory store keeps the File reference alive for the current
 * session so the consumer's publish/save pipeline can upload them later.
 *
 * This module is the single shared registry: both the creator and the artist
 * profile editor import it (directly or via re-export) so a File registered by
 * the MediaForm can be retrieved at save time.
 */

export interface PendingFileMetadata {
  mimeType: string;
  filename: string;
  size: number;
  isVideo: boolean;
}

const pendingFiles = new Map<string, File>();
const pendingFilesMetadata = new Map<string, PendingFileMetadata>();

/** Register a File under its blob URL so it can be retrieved later. */
export function registerPendingFile(blobUrl: string, file: File): void {
  pendingFiles.set(blobUrl, file);
  pendingFilesMetadata.set(blobUrl, {
    mimeType: file.type,
    filename: file.name,
    size: file.size,
    isVideo: file.type.startsWith('video/'),
  });
}

/** Retrieve a previously registered File by its blob URL. */
export function getPendingFile(blobUrl: string): File | undefined {
  return pendingFiles.get(blobUrl);
}

/** Retrieve metadata for a pending file without accessing the File itself. */
export function getPendingFileMetadata(blobUrl: string): PendingFileMetadata | undefined {
  return pendingFilesMetadata.get(blobUrl);
}

/** Check whether a blob URL has a registered pending File. */
export function hasPendingFile(blobUrl: string): boolean {
  return pendingFiles.has(blobUrl);
}

/** Remove a pending File entry and revoke its blob URL. */
export function removePendingFile(blobUrl: string): void {
  pendingFiles.delete(blobUrl);
  pendingFilesMetadata.delete(blobUrl);
  URL.revokeObjectURL(blobUrl);
}

/** Check if a blob URL points to a pending video file. */
export function isPendingVideo(url: string): boolean {
  const meta = pendingFilesMetadata.get(url);
  return !!meta && meta.isVideo;
}

/** Revoke all blob URLs and clear both pending Maps. Call on publish success or modal close. */
export function clearAllPendingFiles(): void {
  for (const blobUrl of pendingFiles.keys()) {
    URL.revokeObjectURL(blobUrl);
  }
  pendingFiles.clear();
  pendingFilesMetadata.clear();
}

/**
 * Convert a File to a base64 data URL for persistent storage in drafts
 */
export async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Get file extension from MIME type
 */
function getExtensionFromMime(mime: string): string {
  const mimeToExt: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'video/mp4': 'mp4',
  };
  return mimeToExt[mime] || 'jpg';
}

/**
 * Convert a base64 data URL back to a File object for upload
 */
export function dataUrlToFile(dataUrl: string, filename?: string): File {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  // Generate filename with correct extension if not provided
  const ext = getExtensionFromMime(mime);
  const finalFilename = filename || `image-${Date.now()}.${ext}`;

  return new File([u8arr], finalFilename, { type: mime });
}

/**
 * Check if a URL is a data URL (base64 encoded)
 */
export function isDataUrl(url: string): boolean {
  return url.startsWith('data:');
}

/**
 * Check if a URL is a valid persistent URL (http/https or data URL)
 */
export function isPersistentUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:');
}
